import { subscribeBrowseReset } from "@-/dev.improve.haniverse.routes.nav._lib.primary-navigation-strip";
import {
  LiquidGlassView as LiquidGlassViewFromLiquidGlass,
  isLiquidGlassSupported,
} from "@-/com.npmjs.callstack__liquid-glass";
import { Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { FrostedSurface } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { useCSSVariable, useUniwind, withUniwind } from "@-/com.npmjs.uniwind";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MaterialTopTabBarProps } from "@-/com.npmjs.expo-router/material-top-tabs";
import type { NavigationRoute, ParamListBase } from "@-/com.npmjs.expo-router/react-navigation";
import { useContext, useEffect } from "react";
import {
  FLOATING_TAB_BAR_BOTTOM_GAP_PX,
  FLOATING_TAB_BAR_HEIGHT_PX,
  PrimaryNavigationStripFadeInsetContext,
} from "@-/dev.improve.haniverse.lib.primary-navigation-strip";

const CSS_COLOR_PREFIX_PATTERN = /^(#|rgb|hsl)/;

const LiquidGlassView = withUniwind(LiquidGlassViewFromLiquidGlass);
const TabBarSurface = Platform.OS === "android" ? FrostedSurface : LiquidGlassView;

export function BrowseMaterialTabBar(props: MaterialTopTabBarProps) {
  const { descriptors, emitter, jumpTo, state } = props;
  const { theme } = useUniwind();
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const accent = useCSSVariable("--stage-mastered");
  const foreground = useCSSVariable("--color-foreground");
  const foregroundValue = String(foreground ?? "0 0% 9%");
  const foregroundColor = CSS_COLOR_PREFIX_PATTERN.test(foregroundValue)
    ? foregroundValue
    : `hsl(${foregroundValue})`;
  useEffect(() => {
    const reset = () => {
      const route = state.routes.find((entry) => entry.name === "levels");
      if (route) {
        jumpTo(route.key);
      }
    };
    return subscribeBrowseReset(reset);
  }, [jumpTo, state.routes]);
  const focusedRouteName = state.routes[state.index]?.name;
  const routes: NavigationRoute<ParamListBase, string>[] = state.routes;

  return (
    <View
      className="absolute inset-x-5.25"
      // The scene extends beneath the global bar's fade; measure the gap from the bar itself.
      style={{ bottom: FLOATING_TAB_BAR_BOTTOM_GAP_PX + bottomTabFadeInset }}
      testID="browse-tab-bar"
    >
      <TabBarSurface
        className={`flex-row rounded-full p-1 ${!isLiquidGlassSupported && Platform.OS !== "android" ? "border border-border bg-background" : ""}`}
        {...(Platform.OS === "android"
          ? {}
          : ({ colorScheme: theme === "dark" ? "dark" : "light", effect: "clear" } as const))}
        style={{ height: FLOATING_TAB_BAR_HEIGHT_PX }}
        testID="browse-tab-bar-glass"
      >
        {routes
          .filter((route) => route.name !== "index")
          .map((route) => {
            const { options } = descriptors[route.key];
            const selected =
              route.name === (focusedRouteName === "index" ? "levels" : focusedRouteName);
            const color = selected ? String(accent ?? "#f97316") : foregroundColor;
            const label = options.title ?? route.name;
            return (
              <Pressable
                accessibilityLabel={options.tabBarAccessibilityLabel ?? label}
                accessibilityRole="tab"
                accessibilityState={{ selected }}
                className={`min-w-0 flex-1 items-center justify-center rounded-full active:opacity-70 ${selected ? "bg-foreground/10" : ""}`}
                key={route.key}
                // No `onLongPress`: with one set, Pressability turns any press whose release
                // reaches JS 500ms after its grant into a long press and drops `onPress`, so a
                // main-thread stall between DOWN and UP swallows the tab switch (Android Maestro
                // `browse-catalog.yaml`, runs 37893820546 and 37929033050). Nothing listens for
                // `tabLongPress`, and native tab bars switch on release however long the hold.
                onPress={() => {
                  const event = emitter.emit({
                    canPreventDefault: true,
                    target: route.key,
                    type: "tabPress",
                  });
                  if (!selected && !event.defaultPrevented) {
                    // Move the native pager together with its route. A route-only navigation
                    // leaves Android's old page selected while an unvisited catalog mounts.
                    jumpTo(route.key);
                  }
                }}
                testID={`browse-tab-${route.name}`}
              >
                {options.tabBarIcon?.({ color, focused: selected })}
                <Text
                  className="text-center text-[10px] leading-3"
                  numberOfLines={1}
                  style={{ color }}
                >
                  {label}
                </Text>
              </Pressable>
            );
          })}
      </TabBarSurface>
    </View>
  );
}
